import React, { useEffect, useMemo, useState } from 'react';
import { Check, Eraser, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { fileUrl } from '../../salesApi';
import { useT } from '../../salesI18n';
import { fieldsFor } from './openingShapes';
import { fmtInches } from './InchesInput';
import { MarksOverlay } from './MarkedPhoto';

// Marcar medidas por cima da foto: escolhe a medida, toca em 2 pontos → linha com o valor digitado.
export const PhotoMeasureDialog = ({ open, onOpenChange, fileId, type, measures = {}, marks = [], onSave }) => {
  const { t } = useT();
  const fields = useMemo(() => fieldsFor(type), [type]);
  const [local, setLocal] = useState([]);
  const [active, setActive] = useState(fields[0]);
  const [pending, setPending] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLocal((marks || []).filter((m) => fields.includes(m.field)));
    setPending(null);
    setActive(fields.find((f) => !(marks || []).some((m) => m.field === f)) || fields[0]);
  }, [open]);

  const has = (f) => local.some((m) => m.field === f);
  const remove = (f) => setLocal((ms) => ms.filter((m) => m.field !== f));
  const tap = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const pt = { x: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)), y: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)) };
    if (!pending) { setPending(pt); return; }
    const next = [...local.filter((m) => m.field !== active), { field: active, x1: pending.x, y1: pending.y, x2: pt.x, y2: pt.y }];
    setLocal(next);
    setPending(null);
    const nextField = fields.find((f) => !next.some((m) => m.field === f));
    if (nextField) setActive(nextField);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[94vh] overflow-y-auto" data-testid="photo-measure-dialog">
        <DialogHeader><DialogTitle className="font-display">{t.markMeasuresTitle}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-600" data-testid="photo-measure-hint">{pending ? t.markTapSecond : t.markHint}</p>
        <div className="flex flex-wrap gap-1.5" data-testid="photo-measure-fields">
          {fields.map((f) => (
            <span key={f} className="inline-flex items-center">
              <button type="button" onClick={() => { setActive(f); setPending(null); }} aria-pressed={active === f} data-testid={`mark-field-${f}`} className={`inline-flex items-center gap-1 h-8 pl-2.5 pr-2.5 rounded-full border text-[11px] font-semibold transition-colors ${active === f ? 'border-teal-600 bg-teal-600 text-white' : has(f) ? 'border-teal-300 bg-teal-50 text-teal-800' : 'border-slate-200 bg-white/70 text-slate-600'} ${has(f) ? 'rounded-r-none' : ''}`}>
                {has(f) && <Check className="h-3 w-3" />} {t.measureLabels[f].replace(' (pol)', '').replace(' (in)', '')} <span className="font-mono opacity-80">{measures[f] ? `${fmtInches(measures[f])}"` : `· ${t.noValueYet}`}</span>
              </button>
              {has(f) && <button type="button" onClick={() => remove(f)} aria-label={t.removeMark} data-testid={`mark-remove-${f}`} className="h-8 w-7 rounded-r-full border border-l-0 border-teal-300 bg-teal-50 text-teal-800 hover:bg-rose-50 hover:text-rose-700 flex items-center justify-center"><X className="h-3 w-3" /></button>}
            </span>
          ))}
        </div>
        <div className="flex justify-center bg-slate-900/90 rounded-2xl p-2 select-none">
          <div className="relative inline-block cursor-crosshair touch-manipulation" onClick={tap} data-testid="photo-measure-stage">
            <img src={fileUrl(fileId)} alt="" className="block w-auto h-auto max-w-full max-h-[62vh]" draggable={false} />
            <MarksOverlay marks={local} measures={measures} pending={pending} />
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <button type="button" onClick={() => { setLocal([]); setPending(null); setActive(fields[0]); }} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-rose-700" data-testid="photo-measure-clear"><Eraser className="h-3.5 w-3.5" /> {t.clearMarks}</button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" className="h-10" onClick={() => onOpenChange(false)} data-testid="photo-measure-cancel">{t.cancel}</Button>
            <Button type="button" className="h-10 font-semibold" onClick={() => { onSave(local); onOpenChange(false); }} data-testid="photo-measure-save">{t.save} · {t.marksCount(local.length)}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
